import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatMoney } from '@johnsaks/core'
import { useMemo, useState } from 'react'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { DataTable, type DataColumn } from '@/components/data-table'
import { BtnPrimary } from '@/components/field'
import { RowActions, type RowActionItem } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate, type ProductRow } from '@/lib/api'
import { PRODUCT_STATUS } from '@/lib/labels'

const PAGE_SIZE = 25

function urlQ() {
  return new URLSearchParams(window.location.search).get('q')?.trim() ?? ''
}

export function Produits() {
  const toast = useToast()
  const qc = useQueryClient()
  const [archiveId, setArchiveId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const searchQ = urlQ()

  const produits = useQuery({
    queryKey: ['admin-products', searchQ, page],
    queryFn: () => {
      const params = new URLSearchParams()
      params.set('limit', String(PAGE_SIZE))
      params.set('page', String(page))
      if (searchQ) params.set('q', searchQ)
      return api<{ items: ProductRow[]; total: number }>(
        `/admin/products?${params.toString()}`,
      )
    },
  })

  const publish = useMutation({
    mutationFn: (id: string) => apiMutate(`/admin/products/${id}/publish`, 'POST'),
    onSuccess: () => {
      toast.ok('Produit publié')
      void qc.invalidateQueries({ queryKey: ['admin-products'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const unpublish = useMutation({
    mutationFn: (id: string) => apiMutate(`/admin/products/${id}/unpublish`, 'POST'),
    onSuccess: () => {
      toast.ok('Produit dépublié')
      void qc.invalidateQueries({ queryKey: ['admin-products'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const archive = useMutation({
    mutationFn: () => apiMutate(`/admin/products/${archiveId!}`, 'DELETE'),
    onSuccess: () => {
      toast.ok('Produit archivé')
      setArchiveId(null)
      void qc.invalidateQueries({ queryKey: ['admin-products'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const items = produits.data?.items ?? []
  const total = produits.data?.total ?? items.length
  const publies = items.filter((p) => p.status === 'PUBLISHED').length
  const brouillons = items.filter((p) => p.status === 'DRAFT').length

  function actionsFor(p: ProductRow): RowActionItem[] {
    const items: RowActionItem[] = [
      {
        label: 'Modifier',
        onSelect: () => {
          window.location.href = `/produits/${p.id}`
        },
      },
    ]
    if (p.status === 'DRAFT') {
      items.push({
        label: 'Publier',
        onSelect: () => publish.mutate(p.id),
      })
    }
    if (p.status === 'PUBLISHED') {
      items.push({
        label: 'Dépublier',
        onSelect: () => unpublish.mutate(p.id),
      })
    }
    items.push({
      label: 'Archiver',
      danger: true,
      onSelect: () => setArchiveId(p.id),
    })
    return items
  }

  const columns = useMemo<DataColumn<ProductRow>[]>(
    () => [
      {
        header: 'Produit',
        id: 'product',
        cell: (p) => (
          <div className="flex items-center gap-3">
            <div className="size-10 shrink-0 overflow-hidden rounded-sm bg-gris-fond">
              {p.image ? (
                <img src={p.image.src} alt="" className="size-full object-cover" />
              ) : null}
            </div>
            <div className="min-w-0">
              <p className="truncate font-medium text-encre-admin">{p.name}</p>
              <p className="kicker text-second-admin">
                {p.sku} · {p.category.name}
              </p>
            </div>
          </div>
        ),
      },
      {
        header: 'Statut',
        accessorKey: 'status',
        cell: (p) => {
          const st = PRODUCT_STATUS[p.status ?? 'DRAFT'] ?? {
            label: p.status ?? 'Brouillon',
            tone: 'draft',
          }
          return <StatusPill label={st.label} tone={st.tone} />
        },
      },
      {
        header: 'Prix',
        accessorKey: 'priceCents',
        cell: (p) => <span className="tabular-nums">{formatMoney(p.priceCents)}</span>,
      },
      {
        header: 'Stock',
        id: 'stock',
        cell: (p) => (
          <span className="text-second-admin">
            {p.stock.available}
            {p.stock.threshold != null ? ` · seuil ${p.stock.threshold}` : ''}
          </span>
        ),
      },
      {
        header: '',
        id: 'actions',
        cell: (p) => (
          <div className="text-right" onClick={(e) => e.stopPropagation()}>
            <RowActions items={actionsFor(p)} />
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [publish, unpublish],
  )

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-second-admin">
          {produits.isLoading
            ? '…'
            : `${publies} publiés · ${brouillons} brouillons · ${total} au total`}
        </p>
        <a href="/produits/nouveau">
          <BtnPrimary>Nouveau produit</BtnPrimary>
        </a>
      </div>

      <DataTable
        columns={columns}
        data={items}
        getRowId={(p) => p.id}
        isLoading={produits.isLoading}
        isError={produits.isError}
        errorMessage="Impossible de charger les produits."
        emptyTitle="Aucun produit"
        emptyBody="Créez votre première référence catalogue."
        emptyAction={
          <a href="/produits/nouveau">
            <BtnPrimary>Nouveau produit</BtnPrimary>
          </a>
        }
        onRowClick={(p) => {
          window.location.href = `/produits/${p.id}`
        }}
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total,
          onPageChange: setPage,
        }}
      />

      <ConfirmDialog
        open={Boolean(archiveId)}
        title="Archiver ce produit ?"
        body="Le produit sera archivé et retiré du catalogue actif."
        confirmLabel="Archiver"
        danger
        busy={archive.isPending}
        onClose={() => setArchiveId(null)}
        onConfirm={() => archive.mutate()}
      />
    </div>
  )
}
